import React, { useState } from 'react';

const TABS = [
  { id: 'descriptive', label: 'Descriptive Analysis', icon: '📊' },
  { id: 'predictive', label: 'Predictive Insights', icon: '📈' },
  { id: 'evidence', label: 'Evidence-Based Options', icon: '💡' },
  { id: 'quality', label: 'Quality Metrics', icon: '📋' }
];

const IndividualGAD7Analytics = ({ clientData }) => {
  const [activeTab, setActiveTab] = useState('descriptive');

  if (!clientData) return null;

  const {
    clientId, currentScore, firstScore, change, percentChange,
    trend, ratePerMonth, durationDays, assessmentCount,
    minScore, maxScore, currentSeverity, expectedScore,
    expectedSeverity, modelAccuracy, responseCategory,
    firstDate, lastDate, forecastPoints
  } = clientData;

  // Determine treatment focus based on current severity and trend
  const severityWord = currentSeverity.split(' (')[0];
  const isImproving = trend.includes('improv') || trend === 'decreasing';
  const isWorsening = trend.includes('worsen') || trend === 'increasing';

  const getTreatmentFocus = () => {
    if (currentScore <= 4) return 'Maintenance and Relapse Prevention';
    if (currentScore <= 9) return 'Active Intervention and Skill Building';
    if (currentScore <= 14) return 'Comprehensive Evidence-Based Treatment';
    return 'Intensive Treatment with Comprehensive Support';
  };

  const getTreatmentConsiderations = () => {
    if (currentScore <= 4) {
      return isImproving || trend === 'stable' ? {
        primary: [
          'Consider maintaining current treatment approach as client shows positive response',
          'May benefit from relapse prevention planning and anxiety management skill consolidation',
          'Could explore tapering session frequency if sustained improvement continues',
          'Might benefit from developing written anxiety management action plan'
        ],
        interventions: [
          'Brief CBT for anxiety relapse prevention',
          'Mindfulness-based anxiety management approaches',
          'Relaxation training and stress reduction techniques',
          'Support group participation for ongoing recovery'
        ]
      } : {
        primary: [
          'Consider increased monitoring frequency given worsening trend',
          'May benefit from assessment of recent stressors or anxiety triggers',
          'Could explore early anxiety management interventions',
          'Might benefit from cognitive restructuring for emerging worry patterns'
        ],
        interventions: [
          'Brief cognitive behavioral therapy for anxiety',
          'Relaxation training and breathing techniques',
          'Problem-solving therapy approaches',
          'Stress management and coping skills training'
        ]
      };
    }
    if (currentScore <= 9) {
      return isImproving ? {
        primary: [
          'Consider continuing current therapeutic approach as improvement is evident',
          'May benefit from reinforcing anxiety management skills and relaxation strategies',
          'Could explore short-term goal setting for continued progress',
          'Might benefit from enhancing social support systems and stress management'
        ],
        interventions: [
          'Structured CBT protocol for anxiety (12-16 sessions)',
          'Mindfulness-based stress reduction techniques',
          'Progressive muscle relaxation training',
          'Applied relaxation and coping skills development'
        ]
      } : {
        primary: [
          'Consider implementing structured anxiety management strategies',
          'May benefit from cognitive restructuring techniques for worry and anxious thoughts',
          'Could explore establishing regular relaxation and self-care routines',
          'Might benefit from weekly GAD-7 monitoring for closer tracking'
        ],
        interventions: [
          'Comprehensive cognitive behavioral therapy for anxiety',
          'Exposure therapy for specific anxiety triggers',
          'Worry time scheduling and cognitive restructuring',
          'Acceptance and mindfulness-based approaches'
        ]
      };
    }
    if (currentScore <= 14) {
      return isImproving ? {
        primary: [
          'Consider continuing current approach while consolidating anxiety management gains',
          'May benefit from enhancing exposure work with graded anxiety-provoking situations',
          'Could explore addressing avoidance behaviors maintaining anxiety symptoms',
          'Might benefit from mindfulness components if excessive worry is predominant'
        ],
        interventions: [
          'Full protocol CBT for anxiety disorders (16-20 sessions)',
          'Exposure and response prevention techniques',
          'Acceptance and commitment therapy (ACT) approaches',
          'Mindfulness-based cognitive therapy (MBCT) techniques'
        ]
      } : {
        primary: [
          'Consider increasing session frequency if not already weekly',
          'May benefit from medication consultation if not currently prescribed',
          'Could explore implementing structured CBT protocol with homework assignments',
          'Might benefit from addressing life stressors contributing to anxiety'
        ],
        interventions: [
          'CBT combined with medication consultation',
          'Exposure therapy with systematic desensitization',
          'Acceptance and commitment therapy techniques',
          'Metacognitive therapy for worry and rumination'
        ]
      };
    }
    // Severe (15-21)
    return isImproving ? {
      primary: [
        'Consider maintaining current treatment intensity during improvement phase',
        'May benefit from continued cognitive work on catastrophic thinking and worry',
        'Could benefit from gradual exposure work as anxiety tolerance improves',
        'Might benefit from psychiatric medication consultation if not currently prescribed'
      ],
      interventions: [
        'Intensive CBT for anxiety disorders with comprehensive skill building',
        'Combined psychotherapy and medication management approach',
        'Dialectical behavior therapy (DBT) skills for emotional regulation',
        'Acceptance-based approaches with graded exposure work'
      ]
    } : {
      primary: [
        'Consider urgent medication evaluation if not currently on medication',
        'May benefit from temporary increase to twice-weekly sessions',
        'Could benefit from comprehensive anxiety management and coping planning',
        'Might benefit from assessment for higher level of care (IOP, PHP)'
      ],
      interventions: [
        'Immediate psychiatric referral for medication evaluation/optimization',
        'Intensive CBT combined with medication management',
        'Crisis intervention and anxiety management protocols',
        'Comprehensive support planning and resource coordination'
      ]
    };
  };

  const treatmentFocus = getTreatmentFocus();
  const { primary: primaryConsiderations, interventions: interventionOptions } = getTreatmentConsiderations();

  const reassessment = currentScore >= 15 ? 'Weekly' : currentScore >= 10 ? 'Every 2-3 weeks' : 'Monthly';
  const target = currentScore >= 15
    ? '≥3-point reduction or 50% improvement'
    : currentScore >= 5
    ? '≥2-point reduction or movement toward remission'
    : 'Maintain score <5 (remission threshold)';

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-2 mb-6 border-b pb-3">
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
              activeTab === tab.id
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* DESCRIPTIVE TAB */}
      {activeTab === 'descriptive' && (
        <div>
          <h2 className="text-2xl font-bold text-blue-900 mb-4 text-center">Anxiety Assessment Analysis</h2>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
            <p className="text-yellow-800 text-base font-semibold">
              <strong>GAD-7 Scoring Logic:</strong> Severity categories based on Spitzer et al. (2006) validation study and clinical guidelines.
              Scores interpreted as: 0-4 (Minimal), 5-9 (Mild), 10-14 (Moderate), 15-21 (Severe).
            </p>
          </div>

          <div className="grid grid-cols-2 gap-6">
            {/* Assessment History */}
            <div>
              <h3 className="text-lg font-bold text-blue-900 mb-3">Assessment History</h3>
              <div className="text-sm space-y-1 text-gray-700">
                <p><strong>Baseline score:</strong> {firstScore} ({(() => { const [y,m,d] = firstDate.split('-'); return new Date(y, m-1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); })()})</p>
                <p><strong>Current score:</strong> {currentScore} ({(() => { const [y,m,d] = lastDate.split('-'); return new Date(y, m-1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); })()})</p>
                <p><strong>Overall change:</strong> {change >= 0 ? '+' : ''}{change} points</p>
                <p><strong>Lowest score:</strong> {minScore}</p>
                <p><strong>Highest score:</strong> {maxScore}</p>
                <p><strong>Total assessments:</strong> {assessmentCount}</p>
                <p><strong>Assessment period:</strong> {durationDays} days</p>
              </div>
            </div>

            {/* Trend Analysis */}
            <div>
              <h3 className="text-lg font-bold text-blue-900 mb-3">Trend Analysis</h3>
              <div className="p-3 rounded-lg mb-3" style={{
                backgroundColor: isImproving ? 'rgba(46,204,113,0.1)' : isWorsening ? 'rgba(231,76,60,0.1)' : 'rgba(52,152,219,0.1)',
                borderLeft: `4px solid ${isImproving ? '#2ecc71' : isWorsening ? '#e74c3c' : '#3498db'}`
              }}>
                <p className="font-semibold text-sm">
                  {isImproving ? 'Anxiety symptoms are improving, indicating positive treatment response' :
                   isWorsening ? 'Anxiety symptoms are worsening, suggesting treatment adjustment may be needed' :
                   'Anxiety symptoms have been stable without significant change'}
                </p>
              </div>
              <div className="text-sm space-y-1 text-gray-700">
                <p><strong>Rate of change:</strong> {ratePerMonth >= 0 ? '+' : ''}{ratePerMonth.toFixed(1)} points per month</p>
                <p><strong>Pattern stability:</strong> {Math.abs(ratePerMonth) < 1 ? 'Stable pattern' : Math.abs(ratePerMonth) < 3 ? 'Gradual change' : 'Rapid change'}</p>
                <p><strong>Response quality:</strong> {change < 0 ? 'Positive treatment response' : change > 0 ? 'Negative treatment response' : 'Neutral treatment response'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PREDICTIVE TAB */}
      {activeTab === 'predictive' && (
        <div>
          <h2 className="text-2xl font-bold text-blue-900 mb-4 text-center">Predictive Pattern Analysis</h2>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
            <p className="text-yellow-800 text-base font-semibold">
              <strong>Predictive Logic:</strong> Analyses based on published anxiety treatment outcome research and longitudinal assessment patterns.
              Predictions are statistical estimates requiring clinical validation.
            </p>
          </div>

          {expectedScore !== null ? (
            <div className="space-y-6">
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-500 font-semibold">Current Score</p>
                  <p className="text-2xl font-bold text-blue-700">{currentScore}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-500 font-semibold">4-Week Forecast</p>
                  <p className="text-2xl font-bold" style={{ color: expectedScore < currentScore ? '#2ecc71' : expectedScore > currentScore ? '#e74c3c' : '#3498db' }}>
                    {expectedScore.toFixed(1)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-500 font-semibold">Expected Direction</p>
                  <p className="text-2xl font-bold capitalize" style={{ color: expectedScore < currentScore ? '#2ecc71' : expectedScore > currentScore ? '#e74c3c' : '#3498db' }}>
                    {expectedScore < currentScore ? 'Improve' : expectedScore > currentScore ? 'Worsen' : 'Stable'}
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-lg font-bold text-blue-600 mb-3">Anxiety Trajectory Modeling</h3>
                  {forecastPoints.length > 0 && (
                    <table className="w-full text-sm border-collapse mb-4">
                      <thead>
                        <tr className="bg-blue-50">
                          <th className="border border-gray-200 p-2 text-left">Forecast Date</th>
                          <th className="border border-gray-200 p-2 text-left">Predicted Score</th>
                          <th className="border border-gray-200 p-2 text-left">Severity</th>
                        </tr>
                      </thead>
                      <tbody>
                        {forecastPoints.slice(0, 6).map((fp, i) => (
                          <tr key={i} className={i % 2 ? 'bg-gray-50' : ''}>
                            <td className="border border-gray-200 p-2">{fp.dateLabel}</td>
                            <td className="border border-gray-200 p-2">{fp.forecastScore.toFixed(1)}</td>
                            <td className="border border-gray-200 p-2">{fp.severity.split(' (')[0]}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <div className="text-sm space-y-1 text-gray-700">
                    <p><strong>Data Points Used:</strong> {assessmentCount} GAD-7 assessments</p>
                    <p><strong>Model Confidence:</strong> {modelAccuracy}% (statistical estimation)</p>
                    <p><strong>Forecast Horizon:</strong> 8 weeks</p>
                    <p><strong>Sample Adequacy:</strong> {assessmentCount < 5 ? 'Moderate' : assessmentCount < 8 ? 'Good' : 'Excellent'} for pattern detection</p>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-bold text-blue-600 mb-3">Treatment Response Indicators</h3>
                  <div className="text-sm space-y-2 text-gray-700">
                    <p><strong>Response Direction:</strong> {change < 0 ? 'Positive trajectory' : change > 0 ? 'Negative trajectory' : 'Stable trajectory'}</p>
                    <p><strong>Response Magnitude:</strong> {Math.abs(change)} point change from baseline</p>
                    <p><strong>Pattern Consistency:</strong> {isImproving ? 'Consistent improvement' : isWorsening ? 'Consistent worsening' : 'Variable pattern'}</p>
                    <p><strong>Treatment Phase:</strong> {Math.abs(change) > 5 ? 'Acute response phase' : Math.abs(change) < 2 ? 'Maintenance phase' : 'Gradual response phase'}</p>

                    <h4 className="font-semibold mt-3">Literature-Based Outcome Expectations:</h4>
                    <ul className="list-disc ml-5 space-y-1">
                      <li><strong>Response Definition:</strong> ≥50% reduction from baseline typically expected in effective treatment</li>
                      <li><strong>Remission Target:</strong> GAD-7 score &lt;5 represents clinical remission goal</li>
                      <li><strong>Timeline Expectations:</strong> Meaningful improvement often seen within 4-8 weeks with effective interventions</li>
                      <li><strong>Plateau Recognition:</strong> Sustained scores without improvement may indicate need for treatment adjustment</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-blue-50 rounded-lg p-4 text-center text-blue-700">
              Predictive pattern analysis requires at least 3 assessments. Currently have {assessmentCount} assessments.
              Continue adding GAD-7 scores to enable statistical forecasting.
            </div>
          )}
        </div>
      )}

      {/* EVIDENCE-BASED TAB */}
      {activeTab === 'evidence' && (
        <div>
          <h2 className="text-2xl font-bold text-blue-900 mb-4 text-center">Evidence-Based Anxiety Treatment Considerations</h2>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
            <p className="text-yellow-800 text-base font-semibold">
              <strong>Treatment Options Notice:</strong> These are evidence-informed considerations based on clinical guidelines and anxiety research literature.
              Treatment selection must be individualized by a licensed clinician.
            </p>
          </div>

          <div className="space-y-6">
            {/* Current severity treatment */}
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="text-lg font-bold text-gray-900 mb-1">{severityWord} Anxiety — Treatment Focus: {treatmentFocus}</h3>
              <p className="text-sm text-gray-500 mb-3">Current severity: {currentSeverity} | Trend: {trend}</p>

              <div className="text-sm space-y-3 text-gray-700">
                <div>
                  <p className="font-semibold">Primary Clinical Considerations:</p>
                  <ul className="list-disc ml-5 mt-1 space-y-1">
                    {primaryConsiderations.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                </div>
                <div>
                  <p className="font-semibold">Evidence-Based Intervention Options:</p>
                  <ul className="list-disc ml-5 mt-1 space-y-1">
                    {interventionOptions.map((o, i) => <li key={i}>{o}</li>)}
                  </ul>
                </div>
                <p className="italic text-gray-500">Clinical Judgment Required: Treatment selection must be individualized based on client presentation, preferences, and clinician assessment.</p>
              </div>
            </div>

            {/* Measurement-Based Care */}
            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Measurement-Based Care Considerations</h3>
              <div className="text-sm space-y-2 text-gray-700">
                <p><strong>GAD-7 Reassessment:</strong> {reassessment} monitoring may be appropriate for this severity level</p>
                <p><strong>Progress Evaluation:</strong> Consider comprehensive review after 4-6 weeks of consistent treatment</p>
                <p><strong>Treatment Adjustment:</strong> May warrant consideration if minimal improvement after 6-8 weeks</p>
                <p><strong>Safety Monitoring:</strong> Standard anxiety assessment as clinically indicated</p>
                <p><strong>Outcome Target:</strong> {target}</p>
                <p><strong>Remission Goal:</strong> GAD-7 score &lt;5 represents clinical remission</p>
              </div>
            </div>

            {/* Treatment Adjustment */}
            <div className="border-l-4 border-orange-400 pl-4">
              <h3 className="text-lg font-bold text-gray-900 mb-2">Treatment Adjustment Considerations</h3>
              <div className="text-sm text-gray-700">
                <p className="font-semibold mb-1">Indicators for Treatment Review:</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li>Less than 25% reduction in GAD-7 score after 6-8 weeks of consistent treatment</li>
                  <li>Initial improvement followed by lack of continued progress for 3+ assessments</li>
                  <li>Increase in GAD-7 score for 2+ consecutive assessments or return to higher severity</li>
                  <li>Worsening in daily functioning despite stable or improving scores</li>
                  <li>Treatment adherence challenges or frequent missed sessions</li>
                </ul>
                <p className="font-semibold mt-3 mb-1">Potential Adjustment Options:</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li>Consider increasing session frequency, duration, or adding between-session contact</li>
                  <li>May benefit from switching therapeutic approaches (e.g., CBT to ACT, or adding exposure techniques)</li>
                  <li>Could explore adding medication consultation or other adjunctive interventions</li>
                  <li>May warrant addressing practical, motivational, or therapeutic relationship factors</li>
                  <li>Might benefit from considering higher intensity services (IOP, PHP) for severe presentations</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* QUALITY METRICS TAB */}
      {activeTab === 'quality' && (
        <div>
          <h2 className="text-2xl font-bold text-blue-900 mb-4 text-center">Practice Quality & Outcome Monitoring</h2>

          <div className="space-y-6">
            {/* Quality Assurance */}
            <div>
              <h3 className="text-lg font-bold text-blue-600 mb-3">Quality Assurance Metrics</h3>
              <div className="text-sm space-y-2 text-gray-700">
                <p><strong>GAD-7 Tracking:</strong> {assessmentCount} documented anxiety assessments over {durationDays} days</p>
                <p><strong>Trend Analysis:</strong> {change < 0 ? 'Positive response pattern' : change > 0 ? 'Concerning pattern requiring attention' : 'Stable pattern'} documented</p>
                <p><strong>Data Quality:</strong> Complete assessment timeline with no missing critical measurements</p>

                <h4 className="font-semibold mt-3">Quality Improvement Opportunities:</h4>
                <ul className="list-disc ml-5 space-y-1">
                  <li>Consider systematic GAD-7 tracking protocols across practice</li>
                  <li>Track improvement rates for evidence-based care reporting</li>
                  <li>Ensure interventions match severity levels and treatment guidelines</li>
                  <li>Implement systematic review procedures for concerning trends</li>
                </ul>

                <h4 className="font-semibold mt-3">Professional Development Considerations:</h4>
                <ul className="list-disc ml-5 space-y-1">
                  <li>Consider specialized training for predominant severity presentations</li>
                  <li>Training in systematic anxiety outcome monitoring may be beneficial</li>
                  <li>Consider certification in structured anxiety treatment approaches</li>
                  <li>Advanced training in exposure techniques may be valuable for moderate-severe cases</li>
                </ul>
              </div>
            </div>

            {/* Clinical Outcome */}
            <div>
              <h3 className="text-lg font-bold text-blue-600 mb-3">Clinical Outcome Assessment</h3>
              <div className="text-sm space-y-2 text-gray-700">
                <p><strong>Response Category:</strong> {responseCategory} (based on standardized criteria)</p>
                <p><strong>Score Change:</strong> {change >= 0 ? '+' : ''}{change} points from baseline ({percentChange >= 0 ? '+' : ''}{percentChange.toFixed(1)}% change)</p>
                <p><strong>Remission Status:</strong> {currentScore < 5 ? 'Achieved' : 'Not yet achieved'} (GAD-7 &lt;5 = remission)</p>
                <p><strong>Clinical Significance:</strong> {Math.abs(change) >= 5 ? 'Clinically significant improvement' : Math.abs(change) >= 3 ? 'Moderate improvement' : 'Minimal change'}</p>

                <h4 className="font-semibold mt-3">Outcome Benchmarking:</h4>
                <ul className="list-disc ml-5 space-y-1">
                  <li><strong>Response:</strong> ≥50% reduction from baseline (research standard)</li>
                  <li><strong>Partial Response:</strong> 25-49% reduction from baseline</li>
                  <li><strong>Remission Target:</strong> GAD-7 score &lt;5 (clinical guideline threshold)</li>
                  <li><strong>Reliable Change:</strong> ≥5 point change considered clinically meaningful</li>
                </ul>
              </div>
            </div>

            {/* Clinical Reference */}
            <div>
              <h3 className="text-lg font-bold text-blue-600 mb-3">GAD-7 Clinical Reference</h3>
              <div className="text-sm text-gray-700">
                <p className="font-semibold mb-1">Severity Level Interpretations:</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li><strong>Minimal (0-4):</strong> No or minimal anxiety requiring no treatment</li>
                  <li><strong>Mild (5-9):</strong> Watchful waiting; repeat GAD-7 at follow-up</li>
                  <li><strong>Moderate (10-14):</strong> Treatment plan considering counseling and/or pharmacotherapy</li>
                  <li><strong>Severe (15-21):</strong> Active treatment with pharmacotherapy and/or psychotherapy</li>
                </ul>

                <p className="font-semibold mt-3 mb-1">GAD-7 Item Domains:</p>
                <ul className="list-disc ml-5 space-y-1">
                  <li><strong>Item 1:</strong> Feeling nervous, anxious, or on edge</li>
                  <li><strong>Item 2:</strong> Not being able to stop or control worrying</li>
                  <li><strong>Item 3:</strong> Worrying too much about different things</li>
                  <li><strong>Item 4:</strong> Trouble relaxing</li>
                  <li><strong>Item 5:</strong> Being so restless that it's hard to sit still</li>
                  <li><strong>Item 6:</strong> Becoming easily annoyed or irritable</li>
                  <li><strong>Item 7:</strong> Feeling afraid as if something awful might happen</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndividualGAD7Analytics;
